import {
  Anchor,
  Box,
  Breadcrumbs,
  Paper,
  Select,
  Text,
  Title,
} from "@mantine/core";
import type { SelectProps } from "@mantine/core";
import type { FC, ReactNode } from "react";
import { Link } from "react-router-dom";

import { DEFAULT } from "routes/route.constant";
import { useScreen } from "utils/hooks";

import classes from "./PageHeader.module.css";

type BreadcrumbItem = {
  title: string;
  href?: string;
};

type PageHeaderProps = {
  title?: string;
  breadcrumbs?: BreadcrumbItem[];
  rightSection?: ReactNode;
  showDateFilter?: boolean;
  homeLabel?: string;
};

type PageHeaderDateFilterProps = Omit<SelectProps, "data"> & {
  data?: SelectProps["data"];
};

const getDefaultYearOptions = () => {
  const currentYear = new Date().getFullYear();

  return Array.from({ length: 3 }, (_, index) => `${currentYear - 2 + index}`);
};

export const PageHeaderDateFilter: FC<PageHeaderDateFilterProps> = ({
  data = getDefaultYearOptions(),
  placeholder = "Ch\u1ecdn th\u1eddi gian",
  ...props
}) => (
  <Select
    className={classes["date-filter"]}
    data={data}
    placeholder={placeholder}
    size="xs"
    allowDeselect={false}
    {...props}
  />
);

const PageHeader: FC<PageHeaderProps> = ({
  title,
  breadcrumbs = [],
  rightSection,
  showDateFilter = true,
  homeLabel = "Trang ch\u1ee7",
}) => {
  const largeScreen = useScreen();
  const pageTitle = title || breadcrumbs[breadcrumbs.length - 1]?.title;
  const headerRightSection =
    rightSection ??
    (showDateFilter ? (
      <PageHeaderDateFilter defaultValue={`${new Date().getFullYear()}`} />
    ) : null);

  return (
    <Paper
      className={classes["page-header"]}
      fz={largeScreen ? "md" : "sm"}
      radius={4}
      shadow="xs"
      withBorder
    >
      <Box className={classes.inner}>
        <Box className={classes["left-section"]}>
          {pageTitle && (
            <Title order={3} className={classes.title}>
              {pageTitle}
            </Title>
          )}

          <Breadcrumbs className={classes.breadcrumbs}>
            <Anchor component={Link} to={DEFAULT}>
              {homeLabel}
            </Anchor>
            {breadcrumbs.map((item, index) =>
              item.href ? (
                <Anchor key={index} component={Link} to={item.href}>
                  {item.title}
                </Anchor>
              ) : (
                <Text key={index} color="blue">
                  {item.title}
                </Text>
              )
            )}
          </Breadcrumbs>
        </Box>

        {headerRightSection && (
          <Box className={classes["right-section"]}>{headerRightSection}</Box>
        )}
      </Box>
    </Paper>
  );
};

export default PageHeader;
